// Migrations multi-base (SA-6) — porté de la maquette Super Admin v2 : l'état RÉEL du schéma de CHAQUE
// base magasin (version lue dans la base, jamais estimée) vs la cible du schéma Drizzle, puis l'application
// des migrations manquantes, base par base (échec = arrêt sur LA base concernée, les autres continuent).
// CIBLAGE PAR CLIENT : un filtre client permet de migrer TOUTES les bases d'un client d'un coup (tester une
// évolution chez le demandeur avant le reste du parc — zéro dérive interne au client).
// PRÉ-VOL OBLIGATOIRE : toute migration passe par la modale de bilan de santé (mesures réelles : latence,
// version PG, transactions longues, verrous, vacuum, taille, gardes) — un `fail` BLOQUE le lancement.
// ⚠️ §23.0 : la maquette montrait aussi les « vagues », le verrou du store privé et les étapes « contract »
// — mécanismes NON branchés aujourd'hui → ils ne sont PAS affichés (jamais simulés). Les étapes du schéma
// affichées sont les migrations RÉELLES du package, le rapport d'exécution est celui du serveur.
import { dateTimeSec } from '@orgazone/date-format';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { api, type MigrationsStatus, type MigrationStoreState, type MigrationRunResult, type StorePreflight, type SnapshotRow } from '../api';
import { useAuth, isSuperadmin } from '../auth';
import { alert, confirm, showToast, Switch, useOverlayPanel } from '@orgazone/ui-web';
import { EmptyState } from '@orgazone/ui-web';

import { Pill, Spin } from '../ui';
import { PurgeJournalCard } from './migrations-purge-journal';


// État d'une base pour l'affichage (pastille + libellé) — dérivé du status réel, 3 cas seulement.
type BaseState = 'ok' | 'late' | 'down';
const stateOf = (s: MigrationStoreState): BaseState => (s.error ? 'down' : s.upToDate ? 'ok' : 'late');
const STATE: Record<BaseState, { label: string; bg: string; fg: string; dot: string }> = {
  ok: { label: 'à jour', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', dot: 'var(--chart-ok)' },
  late: { label: 'en retard', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)', dot: 'var(--chart-wa)' },
  down: { label: 'injoignable', bg: 'var(--da-bg)', fg: 'var(--da-fg)', dot: 'var(--chart-da)' },
};
// Couleurs des contrôles du pré-vol (mêmes tokens que les pastilles d'état).
const CHECK: Record<'ok' | 'warn' | 'fail', { dot: string; fg: string }> = {
  ok: { dot: 'var(--chart-ok)', fg: 'var(--ok-fg)' },
  warn: { dot: 'var(--chart-wa)', fg: 'var(--wa-fg)' },
  fail: { dot: 'var(--chart-da)', fg: 'var(--da-fg)' },
};

type Filter = 'all' | 'late' | 'down' | 'ok';
const FILTERS: { k: Filter; label: string }[] = [
  { k: 'all', label: 'Toutes' }, { k: 'late', label: 'En retard' }, { k: 'down', label: 'Injoignables' }, { k: 'ok', label: 'À jour' },
];

// ── Modale PRÉ-VOL : bilan de santé RÉEL des bases ciblées, puis lancement. ⚠️ Définie AU NIVEAU MODULE
// (jamais pendant le rendu — React démonterait son sous-arbre). Un `fail` sur une base BLOQUE le bouton :
// on ne migre pas une base en contention ou sur un Postgres incompatible « pour voir ». ────────────────
function PreflightModal({ title, storeIds, onClose, onDone }: {
  title: string; storeIds: string[]; onClose: () => void; onDone: (results: MigrationRunResult[]) => void;
}) {
  const [pre, setPre] = useState<StorePreflight[] | null>(null);
  const [running, setRunning] = useState(false);
  const [snapshot, setSnapshot] = useState(true); // filet par défaut : instantané avant migration
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant la migration, comme le clic sur le fond : la fermer cacherait un travail en cours.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => running });
  const titleId = useId();

  useEffect(() => {
    let alive = true;
    api.migrationsPreflight(storeIds)
      .then((r) => { if (alive) setPre(r.results); })
      // ⚠️ L'ÉCHEC PASSE PAR LE CANAL PARTAGÉ (§24.1quater D-bis), jamais par un encart rouge dans la
      // modale : sans bilan de santé, il n'y a RIEN à décider ici — la modale n'a plus de raison d'être
      // ouverte, et elle se referme sur le message.
      .catch((e) => {
        if (!alive) return;
        void alert({ title: 'Bilan de santé impossible', message: `${(e as Error).message}\n\nAucune migration n'a été lancée.` });
        onClose();
      });
    return () => { alive = false; };
  }, [storeIds, onClose]);

  const fails = pre?.filter((p) => p.overall === 'fail').length ?? 0;
  const warns = pre?.filter((p) => p.overall === 'warn').length ?? 0;
  const ready = !!pre && fails === 0 && !running;

  const launch = async () => {
    setRunning(true);
    try { onDone((await api.migrationsRun(storeIds, snapshot)).results); }
    catch (e) {
      // Le lancement lui-même a échoué : rien n'a été tenté sur les bases, et le motif RÉEL le dit.
      void alert({ title: 'Migration non lancée', message: `${(e as Error).message}\n\nAucune base n'a été touchée.` });
      setRunning(false);
    }
  };

  return (
    <div className="modalwrap" onClick={running ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        style={{ width: 'min(94vw, 560px)', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <h3 id={titleId} style={{ marginBottom: 4 }}>{title}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 12 }}>
          Bilan de santé mesuré à l'instant sur chaque base — la migration ne part que si aucun contrôle n'est en échec.
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {!pre && <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--ink500)', padding: '14px 2px' }}><Spin /> Mesures en cours sur {storeIds.length} base{storeIds.length > 1 ? 's' : ''}…</div>}
          {pre?.map((p) => (
            <div key={p.id} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', marginBottom: 10, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', background: 'var(--ink100)' }}>
                <span className="dot" style={{ background: CHECK[p.overall].dot }} />
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, flex: 1 }}>{p.name}</span>
                <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>GLN {p.gln}</span>
                <span style={{ fontSize: 'var(--t-sub)', fontWeight: 700, color: CHECK[p.overall].fg }}>
                  {p.overall === 'ok' ? 'prête' : p.overall === 'warn' ? 'attention' : 'bloquée'}
                </span>
              </div>
              <div style={{ padding: '6px 13px 9px' }}>
                {p.checks.map((c) => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'baseline', gap: 9, padding: '4px 0' }}>
                    <span className="dot" style={{ background: CHECK[c.status].dot, position: 'relative', top: -1 }} />
                    <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, minWidth: 148 }}>{c.label}</span>
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: c.status === 'ok' ? 'var(--ink500)' : CHECK[c.status].fg, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{c.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {pre && (
          <div style={{ fontSize: 'var(--t-note)', color: fails ? 'var(--da-fg)' : 'var(--ink500)', margin: '4px 0 2px', lineHeight: 1.5 }}>
            {fails > 0
              ? `${fails} base${fails > 1 ? 's' : ''} en échec de pré-vol : corrigez la cause (le détail est affiché) puis relancez le bilan.`
              : warns > 0
                ? `${warns} base${warns > 1 ? 's' : ''} avec avertissement — migrable, la migration attendra les transactions en cours.`
                : 'Toutes les bases sont prêtes.'}
          </div>
        )}
        {/* Filet : instantané intégral (copie TEMPLATE côté serveur) AVANT chaque base — s'il échoue, la base
            n'est pas migrée. Coupe brièvement les connexions de la base (la sync des caisses réessaie).
            ⚠️ INTERRUPTEUR et non case à cocher : une option qui décide s'il y aura un filet de secours
            avant une opération irréversible mérite une cible de clic qu'on ne rate pas (la case native
            mesurait 13 px). L'explication passe SOUS la ligne, sur toute la largeur — collée à droite
            d'une commande étroite, elle déséquilibrait le bloc (retour utilisateur 2026-08-11). */}
        <Switch
          checked={snapshot}
          onChange={setSnapshot}
          disabled={running}
          style={{ margin: '10px 0 2px' }}
          label={<>Instantané avant migration <span style={{ fontWeight: 400, color: 'var(--ink500)' }}>(recommandé)</span></>}
          hint="Copie intégrale de chaque base, restaurable depuis la carte « Instantanés ». Sans instantané possible, la base n'est pas migrée."
        />
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose} disabled={running}>Annuler</button>
          <button type="button" className="btn btn-cta" style={{ background: ready ? 'var(--cta)' : undefined, color: ready ? 'var(--cta-ink)' : undefined }} disabled={!ready} onClick={() => void launch()}>
            {running ? 'Migration en cours…' : `Migrer ${storeIds.length} base${storeIds.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * La version du code à côté de la cible : « code e6c7a69 », ou « code non déclaré » quand le serveur n'en connaît pas.
 * ⚠️ L'historique des migrations vient du code, donc la bêta et la production le montrent identique : c'est cette
 *    ligne qui dit laquelle on regarde, et pourquoi leurs cibles diffèrent. Jamais une version devinée (§23.0).
 */
export function codeVersionText(codeVersion: string | null): string {
  return codeVersion ? `code ${codeVersion.slice(0, 7)}` : 'code non déclaré';
}

export function Migrations() {
  const { user } = useAuth();
  // Le serveur gate /migrations/run sur console.migration.run (deploy_ops + super-admin) : on n'affiche le
  // bouton qu'à ces rôles (le serveur reste le juge — l'UI évite juste un bouton voué au 403).
  const canRun = isSuperadmin(user) || !!user?.roles.includes('deploy_ops');

  const [st, setSt] = useState<MigrationsStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [tenantId, setTenantId] = useState<string>(''); // '' = tous les clients
  const [preflight, setPreflight] = useState<{ title: string; storeIds: string[] } | null>(null);
  const [report, setReport] = useState<MigrationRunResult[] | null>(null); // dernier rapport d'exécution (réel)
  const [snaps, setSnaps] = useState<SnapshotRow[] | null>(null);
  const [snapErr, setSnapErr] = useState<string | null>(null);
  const [snapBusy, setSnapBusy] = useState<string | null>(null); // nom de l'instantané en cours de restore/drop

  // Le rapport d'exécution est un instantané : changer de périmètre (client) le périme — on l'efface
  // (sinon « migrée v0 → v1 » d'hier se lit comme un état courant sur un autre client).
  useEffect(() => { setReport(null); }, [tenantId]);

  const load = useCallback(async () => {
    setErr(null);
    try { setSt(await api.migrationsStatus()); }
    catch (e) { setErr((e as Error).message); }
    // Instantanés : liste réelle (serveur DB) — un échec est DIT dans la carte, jamais masqué.
    try { setSnaps((await api.migrationsSnapshots()).snapshots); setSnapErr(null); }
    catch (e) { setSnaps(null); setSnapErr((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Restaurer un instantané = REMPLACER la base vivante par la copie (destructif → confirmation forte).
  const restoreSnap = async (sn: SnapshotRow) => {
    const okGo = await confirm({
      title: `Restaurer ${sn.store?.name ?? sn.db} depuis cet instantané ?`,
      message: `La base ${sn.db} sera REMPLACÉE par sa copie du ${dateTimeSec(sn.at)} (v${sn.fromVersion}). Tout ce qui a été écrit depuis — ventes, sync, corrections — sera PERDU. L'instantané est conservé.`,
      confirmLabel: 'Restaurer (destructif)',
    });
    if (!okGo) return;
    setSnapBusy(sn.name);
    try { await api.migrationsRestoreSnapshot(sn.name); showToast(`${sn.db} restaurée à l'état du ${dateTimeSec(sn.at)} (v${sn.fromVersion}).`); await load(); }
    catch (e) { void alert({ title: 'Échec de la restauration', message: (e as Error).message }); }
    finally { setSnapBusy(null); }
  };
  const dropSnap = async (sn: SnapshotRow) => {
    const okGo = await confirm({
      title: 'Supprimer cet instantané ?',
      message: `${sn.name} (${(sn.sizeBytes / (1 << 20)).toFixed(1)} Mo) sera supprimé — il ne pourra plus servir à restaurer.`,
      confirmLabel: 'Supprimer',
    });
    if (!okGo) return;
    setSnapBusy(sn.name);
    try { await api.migrationsDropSnapshot(sn.name); showToast(`Instantané ${sn.name} supprimé.`); await load(); }
    catch (e) { void alert({ title: 'Échec de la suppression', message: (e as Error).message }); }
    finally { setSnapBusy(null); }
  };

  // Clients présents (dérivés du status — pas d'appel séparé, pas de liste inventée).
  const tenants = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of st?.stores ?? []) if (s.tenantId) m.set(s.tenantId, s.tenantName ?? s.tenantId);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [st]);
  const tenantName = tenants.find((t) => t.id === tenantId)?.name;

  // Périmètre courant = bases du client sélectionné (ou toutes) ; le filtre d'état s'applique par-dessus.
  const scoped = useMemo(() => (st?.stores ?? []).filter((s) => !tenantId || s.tenantId === tenantId), [st, tenantId]);
  const counts = useMemo(() => {
    const c = { ok: 0, late: 0, down: 0 };
    for (const s of scoped) c[stateOf(s)]++;
    return c;
  }, [scoped]);
  const total = scoped.length;
  const shown = useMemo(() => scoped.filter((s) => filter === 'all' || stateOf(s) === filter), [scoped, filter]);
  const toMigrate = useMemo(() => scoped.filter((s) => !s.upToDate), [scoped]);

  const onRunDone = (results: MigrationRunResult[]) => {
    setPreflight(null);
    setReport(results);
    void load(); // versions relues dans les bases (jamais déduites du rapport)

    // ⚠️ UNE BASE NON MIGRÉE S'ACQUITTE. Le détail reste dans sa ligne (utile pour balayer le parc),
    // mais le rapport se referme, la page se recharge, et une ligne rouge parmi vingt se rate — c'est
    // arrivé (retour utilisateur 2026-08-11 : l'échec n'était visible que dans la ligne du magasin).
    // Une base restée en arrière ne recevra plus aucune évolution jusqu'à ce que quelqu'un s'en occupe :
    // ça ne peut pas dépendre du fait qu'on ait regardé au bon endroit.
    const failures = results.filter((r) => r.error);
    if (failures.length > 0) {
      void alert({
        title: `${failures.length} base${failures.length > 1 ? 's' : ''} non migrée${failures.length > 1 ? 's' : ''}`,
        // Le motif RÉEL du serveur, base par base — c'est lui qui dit quoi corriger. Les autres bases
        // ont continué (règle SA-6.2) : on le rappelle pour qu'un échec partiel ne se lise pas comme
        // un échec total.
        message: [
          ...failures.map((r) => `• ${r.name} (${r.gln}) — ${r.error}`),
          results.length > failures.length
            ? `\nLes ${results.length - failures.length} autre(s) base(s) ont bien été traitées.`
            : '',
        ].filter(Boolean).join('\n'),
      });
      return;
    }
    const migrees = results.filter((r) => (r.applied?.length ?? 0) > 0).length;
    showToast(migrees > 0 ? `${migrees} base${migrees > 1 ? 's' : ''} migrée${migrees > 1 ? 's' : ''}.` : 'Toutes les bases étaient déjà à jour.');
  };

  const phase: { label: string; bg: string; fg: string } = !st ? { label: '…', bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' }
    : counts.down > 0 ? { label: `${counts.down} base${counts.down > 1 ? 's' : ''} injoignable${counts.down > 1 ? 's' : ''}`, bg: 'var(--da-bg)', fg: 'var(--da-fg)' }
    : counts.late > 0 ? { label: 'déploiement en attente', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' }
    : total === 0 ? { label: 'aucune base en service', bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' }
    : { label: 'parc à jour', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' };

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Migrations</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
            Le schéma évolue à la source (Drizzle) ; chaque base magasin rattrape sa version, indépendamment des autres.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <Pill bg={phase.bg} fg={phase.fg}>{phase.label}</Pill>
      </div>

      {/* ⚠️ LE MESSAGE PART AU CANAL PARTAGÉ, LA PAGE GARDE UN ÉTAT. Un encart rouge brut ici violait la
          règle transverse (toute info/erreur passe par toast ou dialogue, §24.1quater D-bis) ; mais
          supprimer l'encart sans rien mettre laisserait une page VIDE sans raison. Deux choses
          différentes : le MOTIF (dans le dialogue) et l'ÉTAT de l'écran (ici), avec de quoi réessayer. */}
      {!st && err && (
        <div role="status" style={{ padding: 16, color: 'var(--ink500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
          Les versions n’ont pas pu être lues. Rien n’est affiché ci-dessous : ces états viendraient des
          bases elles-mêmes, et elles n’ont pas répondu.
          <div style={{ marginTop: 10 }}>
            <button className="btn-ghost" style={{ height: 30, padding: '0 12px' }} onClick={() => void load()}>Réessayer</button>
          </div>
        </div>
      )}
      {!st && !err && <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--ink500)', padding: 16 }}><Spin /> Lecture des versions dans chaque base…</div>}

      {st && <>
        {/* Déploiements — un schéma Drizzle par ligne (aujourd'hui UNE ligne réelle : les bases magasin retail),
            + le CIBLAGE PAR CLIENT (tester une évolution chez le demandeur avant le reste du parc). */}
        <div className="dcard" style={{ overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Déploiements</span>
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>un schéma Drizzle par ligne · versions indépendantes</span>
            <div style={{ flex: 1, minWidth: 6 }} />
            <label htmlFor="mig-tenant" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Client</label>
            {/* ⚠️ FOND OPAQUE : `--ok-bg` est translucide en thème sombre, et la liste OUVERTE est
                peinte par le système sur sa propre surface claire — l'encre presque blanche y
                deviendrait blanc sur blanc. La teinte qui dit « un client est choisi » reste, mais
                mélangée au papier. Trouvé par la garde `selects-lisibles`, pas à l'œil. */}
            <select id="mig-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}
              style={{ height: 30, padding: '0 10px', borderRadius: 'var(--r-xs)', border: `1px solid ${tenantId ? 'var(--teal)' : 'var(--ink200)'}`, background: tenantId ? 'color-mix(in srgb, var(--teal-500) 13%, var(--paper))' : 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
              <option value="">Tous les clients</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px', flexWrap: 'wrap' }}>
            <span className="dot" style={{ background: counts.down ? 'var(--chart-da)' : counts.late ? 'var(--chart-wa)' : 'var(--chart-ok)' }} />
            <span style={{ minWidth: 132, flex: 1 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700 }}>Bases magasin · Retail{tenantName ? ` — ${tenantName}` : ''}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2 }}>une base Postgres par magasin (mag_&lt;GLN&gt;) · tronc partagé + composition retail</span>
            </span>
            <span className="mono" style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink600)', whiteSpace: 'nowrap' }}>cible v{st.target}</span>
            <span className="mono" data-code-version style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>{codeVersionText(st.codeVersion)}</span>
            <span className="mono" style={{ fontSize: 'var(--t-note)', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 52, textAlign: 'right' }}>{total} base{total > 1 ? 's' : ''}</span>
            <Pill bg={phase.bg} fg={phase.fg}>{counts.late + counts.down === 0 ? 'à jour' : `${counts.late + counts.down} à traiter`}</Pill>
          </div>
        </div>

        {total === 0 ? (
          // État honnête : rien à déployer tant qu'aucun magasin n'est en service (pas d'écran simulé).
          <div className="dcard">
            <EmptyState icon="box" title={tenantId ? 'Ce client n’a aucune base en service' : 'Rien à déployer pour l’instant'}
              note={tenantId ? 'Mettez un de ses magasins en service pour voir sa base ici.' : 'Aucune base en service : rien à migrer. La ligne apparaîtra ici dès le premier magasin mis en service.'} />
          </div>
        ) : <>
          {/* Synthèse live — bases migrées + barre de progression, retards, injoignables (tout est réel). */}
          <div className="dcard" style={{ padding: '18px 20px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span className="mono" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--teal)', whiteSpace: 'nowrap' }}>v{st.target}</span>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 600 }}>{st.migrations[st.migrations.length - 1]?.label ?? 'schéma magasin retail'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: '14px 20px', marginTop: 16 }}>
              <div>
                <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>bases migrées</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 7 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1 }}>{counts.ok}</span>
                  <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>sur {total}</span>
                </div>
                <div style={{ position: 'relative', height: 6, borderRadius: 'var(--r-r3)', background: 'var(--ink100)', marginTop: 10, overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${total ? Math.round((counts.ok / total) * 100) : 0}%`, background: 'var(--chart-ok)', borderRadius: 'var(--r-r3)' }} />
                </div>
              </div>
              <div>
                <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>en retard</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 7 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1, color: counts.late ? 'var(--wa-fg)' : undefined }}>{counts.late}</span>
                </div>
                <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 9, lineHeight: 1.45 }}>Des versions mixtes coexistent par construction pendant un déploiement (expand/contract).</div>
              </div>
              <div>
                <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>injoignables</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 7 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1, color: counts.down ? 'var(--da-fg)' : undefined }}>{counts.down}</span>
                </div>
                <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 9, lineHeight: 1.45 }}>Base qui ne répond pas à la lecture de version — l'erreur réelle est affichée sur la ligne.</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 14, alignItems: 'start' }}>
            {/* Tableau des bases : version réelle, état, erreur réelle, migration ciblée (via pré-vol). */}
            <div className="dcard" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Bases magasin{tenantName ? ` — ${tenantName}` : ''}</span>
                <div style={{ flex: 1, minWidth: 6 }} />
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {FILTERS.map((f) => (
                    <button aria-pressed={filter === f.k} key={f.k} onClick={() => setFilter(f.k)}
                      className={filter === f.k ? 'chip info active' : 'chip info'} style={{ height: 26, padding: '0 10px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)' }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              {shown.length === 0 && <EmptyState compact icon="box" title="Aucune base dans ce filtre" note="Changez de filtre ou de client pour voir les autres bases." />}
              {shown.map((s) => {
                const state = STATE[stateOf(s)];
                const res = report?.find((r) => r.id === s.id);
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 18px', borderTop: '1px solid var(--ink100)', flexWrap: 'wrap' }}>
                    <span className="dot" style={{ background: state.dot }} />
                    <div style={{ minWidth: 130, flex: 1 }}>
                      <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{s.name}</div>
                      <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{s.tenantName ?? '—'} · GLN {s.gln}</div>
                    </div>
                    <div style={{ minWidth: 96 }}>
                      <div className="mono" style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: s.version === null ? 'var(--ink400)' : s.upToDate ? 'var(--ink700)' : 'var(--wa-fg)' }}>
                        {s.version === null ? '—' : `v${s.version}`}<span style={{ color: 'var(--ink400)', fontWeight: 500 }}> / v{st.target}</span>
                      </div>
                      {/* ⚠️ LE NUMÉRO NE SUFFIT PAS À DIRE « À JOUR ». Une table neuve arrive par la
                          RÉCONCILIATION, qui ne fait pas bouger la version : une base pouvait afficher
                          v5/v5 tout en n'ayant jamais reçu quatre tables, et l'écran qui les lit échouait
                          des semaines plus tard sur « relation … does not exist ». Ce qui manque se DIT,
                          à côté du numéro qui, lui, a l'air bon. */}
                      {s.missingTables && s.missingTables.length > 0 && (
                        <div
                          title={`Tables absentes de cette base : ${s.missingTables.join(', ')}`}
                          style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', marginTop: 2 }}
                        >
                          {s.missingTables.length} {s.missingTables.length > 1 ? 'tables manquantes' : 'table manquante'}
                        </div>
                      )}
                      {/* ⚠️ LE MANQUE LE PLUS FRÉQUENT : une colonne neuve arrive par la RÉCONCILIATION,
                          qui ne fait pas bouger le numéro. Sans cette ligne, la base affichait « à jour »
                          et l'écran qui lit la colonne échouait — sans qu'aucune migration ne soit offerte. */}
                      {s.missingColumns && s.missingColumns.length > 0 && (
                        <div
                          title={`Colonnes absentes de cette base : ${s.missingColumns.join(', ')}`}
                          style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', marginTop: 2 }}
                        >
                          {s.missingColumns.length} {s.missingColumns.length > 1 ? 'colonnes manquantes' : 'colonne manquante'}
                        </div>
                      )}
                      {/* ⚠️ UNE COLONNE AU MAUVAIS TYPE EST INVISIBLE À LA SONDE DE PRÉSENCE, et c'est
                          elle qui a laissé trois bases en « à jour » pendant que l'écran d'inventaire
                          refusait de s'ouvrir (« operator does not exist: bigint < timestamp with time
                          zone », 2026-09-03). Le voyant vert était le vrai problème : il empêchait le
                          seul geste qui débloquait la situation. */}
                      {s.wrongTypes && s.wrongTypes.length > 0 && (
                        <div
                          title={`Colonnes au mauvais type : ${s.wrongTypes.join(' · ')}`}
                          style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', marginTop: 2 }}
                        >
                          {s.wrongTypes.length} {s.wrongTypes.length > 1 ? 'colonnes au mauvais type' : 'colonne au mauvais type'}
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 112 }}>
                      <Pill bg={state.bg} fg={state.fg}>{state.label}</Pill>
                    </div>
                    {canRun && !s.upToDate && (
                      <button onClick={() => setPreflight({ title: `Migrer — ${s.name}`, storeIds: [s.id] })} disabled={preflight !== null} className="btn-ghost danger" style={{ height: 28, padding: '0 10px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Migrer cette base
                      </button>
                    )}
                    {s.error && <div className="mono" style={{ flexBasis: '100%', fontSize: 'var(--t-sub)', color: 'var(--da-fg)', lineHeight: 1.55, overflowWrap: 'anywhere' }}>{s.error}</div>}
                    {res && (res.error
                      ? <div className="mono" style={{ flexBasis: '100%', fontSize: 'var(--t-sub)', color: 'var(--da-fg)', lineHeight: 1.55, overflowWrap: 'anywhere' }}>échec {res.fromVersion !== null ? `en v${(res.toVersion ?? res.fromVersion) + 1}` : '(injoignable)'} : {res.error}</div>
                      : (res.applied?.length ?? 0) > 0
                        ? <div className="mono" style={{ flexBasis: '100%', fontSize: 'var(--t-sub)', color: 'var(--ok-fg)', lineHeight: 1.55 }}>migrée v{res.fromVersion} → v{res.toVersion} ({res.applied!.map((a) => a.label).join(', ')}){res.snapshot ? ` · instantané ${res.snapshot}` : ''}</div>
                        : <div className="mono" style={{ flexBasis: '100%', fontSize: 'var(--t-sub)', color: 'var(--ink500)', lineHeight: 1.55 }}>déjà à jour — rien appliqué</div>)}
                  </div>
                );
              })}
              <div style={{ padding: '11px 18px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <span className="dot" style={{ background: 'var(--chart-in)' }} />
                <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 }}>
                  Des bases en versions différentes coexistent par construction pendant un déploiement. Seuls un échec ou une base injoignable méritent une alerte.
                </span>
              </div>
            </div>

            {/* Colonne droite : lancer le déploiement (périmètre courant) + les étapes RÉELLES du schéma. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="dcard" style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 8 }}>Déployer{tenantName ? ` — ${tenantName}` : ''}</div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5, marginBottom: 12 }}>
                  {tenantName
                    ? `Migre TOUTES les bases en retard de ${tenantName} d'un coup (zéro dérive interne au client), après le bilan de pré-vol.`
                    : 'Applique les migrations manquantes sur toutes les bases en retard, après le bilan de pré-vol. Chaque migration est une transaction ; un échec n\'arrête pas les autres bases.'}
                </div>
                {canRun ? (
                  <button onClick={() => setPreflight({ title: tenantName ? `Migrer les bases de ${tenantName}` : 'Migrer les bases en retard', storeIds: toMigrate.map((s) => s.id) })}
                    disabled={preflight !== null || toMigrate.length === 0}
                    style={{ width: '100%', height: 38, borderRadius: 'var(--r-sm)', border: 'none', background: toMigrate.length === 0 ? 'var(--ink100)' : 'var(--cta)', color: toMigrate.length === 0 ? 'var(--ink400)' : 'var(--cta-ink)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: toMigrate.length === 0 ? 'default' : 'pointer', boxShadow: toMigrate.length === 0 ? 'none' : 'var(--cta-sh)' }}>
                    {toMigrate.length === 0 ? (tenantName ? `${tenantName} — à jour` : 'Parc à jour — rien à migrer') : `Pré-vol puis migrer ${toMigrate.length} base${toMigrate.length > 1 ? 's' : ''}`}
                  </button>
                ) : (
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lancement réservé aux rôles deploy_ops et super-admin (lecture seule pour votre rôle).</div>
                )}
              </div>

              <div className="dcard" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink200)' }}>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>Étapes du schéma</span>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Pill bg="var(--in-bg)" fg="var(--in-fg)">expand</Pill>
                    <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>additif · idempotent · une transaction par étape</span>
                  </div>
                  {st.migrations.map((m) => (
                    <div key={m.version} style={{ display: 'flex', gap: 9, padding: '5px 0' }}>
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', flexShrink: 0 }}>v{m.version}</span>
                      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink700)', lineHeight: 1.45, minWidth: 0 }}>{m.label} <span className="mono" style={{ color: 'var(--ink400)', fontSize: 'var(--t-micro)' }}>· {m.statements} instr.</span></span>
                    </div>
                  ))}
                  <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 10, lineHeight: 1.5 }}>
                    Une migration livrée ne se réécrit jamais : on ajoute une étape. Les étapes « contract » (destructives) n'existent pas encore.
                  </div>
                </div>
              </div>

              {/* Journal des bases : l'élagage de ce que toutes les caisses ont lu — le geste que la route du BO attendait. */}
              <PurgeJournalCard stores={scoped} canRun={canRun} />

              {/* Instantanés : copies intégrales pré-migration (filet tant que le PITR n'est pas monté). */}
              <div className="dcard" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>Instantanés</span>
                  {snaps && <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{snaps.length}</span>}
                </div>
                <div style={{ padding: '10px 16px 12px' }}>
                  {snapErr ? <div style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)', lineHeight: 1.5 }}>Liste indisponible : {snapErr}</div>
                    : !snaps ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink500)', fontSize: 'var(--t-note)' }}><Spin size={12} /> Lecture…</div>
                    : snaps.length === 0 ? <EmptyState compact icon="history" title="Aucun instantané" note="Il s’en crée un avant chaque migration (case cochée au pré-vol) — restaurable ici." />
                    : snaps.map((sn) => (
                      <div key={sn.name} style={{ padding: '8px 0', borderBottom: '1px solid var(--ink100)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, flex: 1, minWidth: 90 }}>{sn.store?.name ?? sn.db}</span>
                          <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>v{sn.fromVersion} · {(sn.sizeBytes / (1 << 20)).toFixed(1)} Mo</span>
                        </div>
                        <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', marginTop: 2 }}>{new Date(sn.at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</div>
                        {canRun && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <button onClick={() => void restoreSnap(sn)} disabled={snapBusy !== null} className="btn-ghost danger" style={{ height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-sub)', fontWeight: 600 }}>
                              {snapBusy === sn.name ? '…' : 'Restaurer'}
                            </button>
                            <button onClick={() => void dropSnap(sn)} disabled={snapBusy !== null} className="btn-ghost" style={{ height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-sub)', fontWeight: 600 }}>
                              Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 8, lineHeight: 1.5 }}>
                    Restaurer REMPLACE la base vivante par la copie — tout l'écrit postérieur est perdu. Filet de bêta : le vrai rollback des données sera le PITR (archivage WAL, cadrage infra).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}
      </>}

      {preflight && <PreflightModal title={preflight.title} storeIds={preflight.storeIds} onClose={() => setPreflight(null)} onDone={onRunDone} />}
    </div>
  );
}
